import { Form, Formik, Field as FormikField } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { login } from "../../../../features/auth/authSlice";
import useError from "../../../../hooks/useError";
import { post } from "../../../../logic/util/hooks";
import Button from "../../Button";
import StaticAlert from "../../atoms/StaticAlert";
import Field from "../Field";
import PasswordField from "../PasswordField";
import mixpanel from "mixpanel-browser";
import cross from "../../../assets/icons/cross.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";

interface SignInFormProps {
  content: string;
}
interface SSOModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const initialSignIn = {
  email: "",
  password: "",
  // staySignIn: true
};
const signInValidation = Yup.object().shape({
  email: Yup.string()
    .email("Error: Enter a valid email address, for example mike@gmail.com")
    .required("Required"),
  password: Yup.string().required("Required"),
  // staySignIn: Yup.boolean().required()
});


const SSOModal: React.FC<SSOModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none bg-black40">
      <div className="bg-white rounded-lg p-6 max-w-3xl max-h-screen w-full mx-4 shadow-lg">
        <button className="flex w-full justify-end mb-2">
          <LazyLoadImage
            src={cross}
            alt="close"
            className="cursor-pointer mt-3 mb-auto"
            onClick={() => onClose()}
          />
        </button>
        <h2 className="text-xl font-bold mb-4">Notice: Your company requires you to use the "Sign up with <br /> Google" option to create your account.</h2>
        <p className="text-gray-700 mb-4">
          Return to the sign up page & click the "Sign up with google" to get started. This requirement <br />was set by your company. If you have questions or concerns
          regarding this, reach out to your<br /> company's IT department or support@bulletproofinbox.com.
        </p>
        <div className="flex justify-around w-full my-10">
          <button
            className="text-white border bg-green_primary rounded-full w-full py-2 font-semibold outline-none mt-3 md:mt-0 max-w-md"
            onClick={() => onClose()}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};


const SignInForm: React.FC<SignInFormProps> = () => {
  const dispatch = useDispatch();
  const { error, mountError } = useError();
  const [showSSOModal, setShowSSOModal] = useState(false);

  // Check if error is the SSO error and show modal
  useEffect(() => {
    // Check if error is an object with props
    if (error && typeof error === 'object' && 'props' in error && error.props?.errors === "Company requires SSO. Please contact your administrator.") {
      setShowSSOModal(true);
    }
  }, [error]);

  const handleCloseSSOModal = () => {
    setShowSSOModal(false);
  };


  let [searchParams] = useSearchParams();
  return (
    <Formik
      initialValues={initialSignIn}
      validationSchema={signInValidation}
      onSubmit={async (values) => {
        try {
          const searchParamName = searchParams.get("redirect");
          const { data } = await post("auth", "bpi/signin", values);
          sessionStorage.setItem("showInfoDialogue", JSON.stringify(null));
          mixpanel.track("Clicked: Sign-In with your email address 'Continue' button", {
            user_Id: data?.id,
            user_Email: data?.email,
            user_FirstName: data?.first,
            user_LastName: data?.last,
            user_Phone: data?.phone,
            user_Tier: data?.tier,
          });
          mixpanel.identify(data.email);
          mixpanel.people.set({
            $email: data?.email,
            $first_name: data?.firstName,
            $last_name: data?.lastName,
            $user_id: data.id,
            // Add more properties as needed
          });
          dispatch(login({ userData: data, token: data.token }));
          if (searchParamName !== null) {
            window.location.assign(`/app/${searchParamName}`);
          } else {
            window.location.assign("/app/email-accounts");
          }
        } catch (error: any) {
          console.log("error", error);
          
          // Check if it's the SSO error
          let errorMessage = '';
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && 'props' in error) {
            errorMessage = error.props?.errors || '';
          } else if (error?.response?.data?.errorMessage) {
            errorMessage = error.response.data.errorMessage;
          }
          
          if (errorMessage) {
            mountError(error);
          } else {

            mountError({
              response: {
                data: {
                  errorMessage:
                    "Email or password doesn't match our records. Please try again or reset your password below",
                },
              },
            });
          }
        }
      }}
    >
      {(props) => {
        return (
          <React.Fragment>
            <Form className="flex flex-col gap-3.5 w-full">
              {error && typeof error === 'object' && 'props' in error && error.props?.errors !== "Company requires SSO. Please contact your administrator." && (
                <StaticAlert type="error" message={error} />
              )}
              <Field
                name="email"
                key="email"
                label="Email"
                type="email"
                placeholder="Email"
              />
              <PasswordField
                name="password"
                key="password"
                label="Password"
                placeholder="Password"
              />
              <div>
                <Button
                  format={{
                    type: "submit",
                    id: "signIn",
                    isFilled: true,
                    content: `Continue`,
                    isLoading: props.isSubmitting,
                    width: "w-full",
                  }}
                />
              </div>
            </Form>
            {/* SSO Modal */}
            <SSOModal isOpen={showSSOModal} onClose={handleCloseSSOModal} />
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

export default SignInForm;
